/**
 * fr3render - the browser-side FastReport 4 (.fr3) renderer.
 *
 * engine/ is a one-to-one JavaScript port of the tablet's Java engine
 * (MyFasManMobile library/services/report/fr3): parser (with inheritance
 * merge), expression evaluator, PascalScript subset, barcodes and layout.
 * Both engines must keep producing the golden fixtures in MyFasManMobile
 * tools/fr3harness/golden (see __tests__/fr3Golden.test.js): any change to
 * the rendering is made, and checked, on BOTH sides.
 *
 * emitPdf.js paints the engine's draw ops with pdf-lib.
 *
 * Interface:
 *
 *   renderReportPreview({
 *     datasets: [{
 *       step, type,      // report_ step (repsubnr order) and 'R' | 'G'
 *       template,        // .fr3 name the dataset prints with
 *       templateXml,     // the .fr3 as text (decoded per its XML prolog)
 *       templateBytes,   // the raw .fr3 bytes (Uint8Array) - preferred
 *       columns, rows,   // [{ column: value }]
 *       binaryColumns,   // columns whose values are base64 (images)
 *       skipped,         // 'frf' | 'no_layout' | 'layout_missing' | null
 *     }],
 *     logo,              // { mime, base64, ... } | null - ticket logo, invoice logo for invoices
 *     title, lang,
 *     fetchParent,       // async (name, childTemplate) => Uint8Array|null (inherited templates)
 *     now,               // optional Date for [Date]/[Time]
 *   }) -> Promise<{ pdf: Uint8Array, pageCount: number, warnings: string[] }>
 *
 * Rejects with an Error carrying a readable message when nothing can be
 * rendered (.frf only, inherited parent missing, unsupported expression ...).
 */
import { Fr3Parser, parentReportName } from './engine/fr3Parser';
import { Fr3Engine, newOptions } from './engine/fr3Engine';
import { Fr3Data } from './engine/fr3Data';
import { emitPdf } from './emitPdf';
import defaultLogoUrl from '../../assets/header/logo_light.png';

export class Fr3RenderNotAvailableError extends Error {
    constructor(message) {
        super(message || 'The .fr3 preview engine is not available');
        this.name = 'Fr3RenderNotAvailableError';
        this.notAvailable = true;
    }
}

/** True: the JS port of the engine is wired in. */
export const isRenderAvailable = () => true;

/** Inheritance depth the parser accepts (Fr3Parser MAX_INHERITANCE_DEPTH). */
const MAX_PARENTS = 4;

const base64ToBytes = (b64) => {
    const binary = atob(String(b64 || '').replace(/\s+/g, ''));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
};

const utf8Bytes = (text) => new TextEncoder().encode(text);

/**
 * Rows as the tablet feeds them (ReportSource.dataset): null -> "", every
 * value a string; the first non-empty value of each binary column decoded
 * into the image map (images repeat identically on every line row).
 */
const prepareData = (dataset) => {
    const binary = new Set((dataset.binaryColumns || []).map((c) => String(c).toLowerCase()));
    const images = new Map();
    const rows = (dataset.rows || []).map((row) => {
        const out = {};
        Object.keys(row || {}).forEach((k) => {
            const raw = row[k];
            const v = raw === null || raw === undefined ? '' : String(raw);
            out[k] = v;
            const key = k.toLowerCase();
            if (v !== '' && binary.has(key) && !images.has(key)) {
                try {
                    images.set(key, base64ToBytes(v));
                } catch (e) {
                    // not valid base64: the picture stays empty
                }
            }
        });
        return out;
    });
    return { rows, images };
};

/** Fetches the parent chain of an inherited template up front: the parser itself is synchronous. */
const loadParents = async (bytes, template, fetchParent, cache) => {
    let name = parentReportName(bytes);
    let child = template;
    for (let depth = 0; name !== '' && depth < MAX_PARENTS; depth++) {
        if (!cache.has(name)) {
            let parent = null;
            if (typeof fetchParent === 'function') {
                try {
                    parent = await fetchParent(name, child);
                } catch (e) {
                    parent = null;
                }
            }
            cache.set(name, parent && parent.length ? parent : null);
        }
        const parentBytes = cache.get(name);
        if (!parentBytes) return;
        child = name;
        name = parentReportName(parentBytes);
    }
};

const SKIP_TEXT = {
    frf: 'FastReport 2 (.frf) layout - incompatible with the preview',
    no_layout: 'no layout (grid step)',
    layout_missing: 'layout file not found',
};

/**
 * Uploaded ticket/invoice logos often carry a wide white or transparent
 * margin. FastReport centres the picture inside the Logo box (Center=True),
 * so that margin pushes the visible logo right and down. Crop it away
 * (browser only; without a canvas the bytes pass through unchanged).
 */
const trimLogoMargins = async (bytes, mime, inset = 0) => {
    if (!bytes || typeof document === 'undefined' || typeof Image === 'undefined') return bytes;
    // jsdom (tests) never loads images: skip instead of waiting forever.
    if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || '')) return bytes;
    try {
        const url = URL.createObjectURL(new Blob([bytes], { type: mime || 'image/png' }));
        const img = await new Promise((resolve, reject) => {
            const i = new Image();
            const timer = setTimeout(() => reject(new Error('logo load timeout')), 3000);
            i.onload = () => { clearTimeout(timer); resolve(i); };
            i.onerror = (e) => { clearTimeout(timer); reject(e); };
            i.src = url;
        });
        URL.revokeObjectURL(url);
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (!w || !h) return bytes;
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return bytes;
        ctx.drawImage(img, 0, 0);
        const px = ctx.getImageData(0, 0, w, h).data;
        let minX = w, minY = h, maxX = -1, maxY = -1;
        for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
                const o = (y * w + x) * 4;
                const blank = px[o + 3] < 16 || (px[o] > 245 && px[o + 1] > 245 && px[o + 2] > 245);
                if (blank) continue;
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
            }
        }
        if (maxX < 0) return bytes;
        const pad = 2;
        minX = Math.max(0, minX - pad);
        minY = Math.max(0, minY - pad);
        maxX = Math.min(w - 1, maxX + pad);
        maxY = Math.min(h - 1, maxY + pad);
        const cw = maxX - minX + 1;
        const ch = maxY - minY + 1;
        // Transparent room below the cropped logo: it scales a little smaller
        // and sits higher in the (centred) Logo box, leaving a gap above the
        // header rule instead of touching it.
        const bottomGap = Math.round(ch * 0.2);
        // `inset`: extra transparent room on every side (a fraction of the
        // logo), so the centred Logo box scales it down a little.
        const padX = Math.round(cw * inset);
        const padY = Math.round(ch * inset);
        const out = document.createElement('canvas');
        out.width = cw + 2 * padX;
        out.height = ch + bottomGap + 2 * padY;
        out.getContext('2d').drawImage(canvas, minX, minY, cw, ch, padX, padY, cw, ch);
        const blob = await new Promise((resolve) => out.toBlob(resolve, 'image/png'));
        return blob ? new Uint8Array(await blob.arrayBuffer()) : bytes;
    } catch (e) {
        return bytes;
    }
};

/**
 * No ticket/invoice logo uploaded for this client: MySoftTouch renders with
 * its own SoftTouch logo (assets/header/logo_light.png) instead of an empty
 * Logo box. MySoftTouch only - the tablet uses what is configured.
 */
const loadDefaultLogo = async () => {
    if (typeof fetch === 'undefined' || !defaultLogoUrl) return null;
    if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || '')) return null;
    try {
        const res = await fetch(defaultLogoUrl);
        if (!res.ok) return null;
        const bytes = new Uint8Array(await res.arrayBuffer());
        // Smaller than an uploaded logo: the wide SoftTouch logo otherwise
        // fills the Logo box up to the header text.
        return await trimLogoMargins(bytes, 'image/png', 0.12);
    } catch (e) {
        return null;
    }
};

export const renderReportPreview = async (input) => {
    const datasets = (input && input.datasets) || [];
    const warnings = [];
    const pages = [];
    const parents = new Map();
    const errors = [];
    const logoBytes = input && input.logo && input.logo.base64
        ? await trimLogoMargins(base64ToBytes(input.logo.base64), input.logo.mime)
        : await loadDefaultLogo();

    for (const ds of datasets) {
        const label = (ds.template || ('step ' + ds.step)) + ': ';
        if (ds.skipped) {
            warnings.push(label + (SKIP_TEXT[ds.skipped] || ds.skipped) + (ds.error ? ' (' + ds.error + ')' : ''));
            continue;
        }
        const bytes = ds.templateBytes && ds.templateBytes.length
            ? ds.templateBytes
            : (ds.templateXml ? utf8Bytes(ds.templateXml) : null);
        if (!bytes) {
            warnings.push(label + SKIP_TEXT.layout_missing);
            continue;
        }
        try {
            await loadParents(bytes, ds.template, input.fetchParent, parents);
            const parser = new Fr3Parser((name) => (parents.has(name) ? parents.get(name) : null));
            const doc = parser.parse(bytes);
            const { rows, images } = prepareData(ds);
            const options = newOptions();
            options.logo = logoBytes;
            images.forEach((v, k) => options.images.set(k, v));
            if (input.now instanceof Date) options.now = input.now;
            // Header variables MyFashionManager sets before printing (RepTitle,
            // RepSelection, RepLicenceName, VarBetmN, VarCodeNOms, ...).
            if (input.variables && typeof input.variables === 'object') {
                Object.keys(input.variables).forEach((k) => {
                    const v = input.variables[k];
                    if (v !== undefined && v !== null) options.variables.set(k, v);
                });
            }
            const engine = new Fr3Engine(doc, Fr3Data.of(rows), options);
            const rendered = engine.render();
            rendered.forEach((p) => pages.push(p));
            engine.warnings.forEach((w) => warnings.push(label + w));
        } catch (e) {
            const message = e && e.message ? e.message : String(e);
            errors.push(label + message);
        }
    }

    if (pages.length === 0) {
        const reasons = errors.concat(warnings);
        throw new Error(reasons.length ? reasons.join(' · ') : 'The report produced no pages');
    }
    errors.forEach((e) => warnings.unshift(e));

    const out = await emitPdf(pages, { title: input.title });
    out.warnings.forEach((w) => warnings.push(w));
    return { pdf: out.pdf, pageCount: pages.length, warnings };
};

export default renderReportPreview;
